import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { INDUSTRIES, type Industry } from "../types";
import "../components/AuthForm.css";

export function SignupPage() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", company: "",
    industry: "" as Industry | "",
    password: "", confirmPassword: "",
    agreedToTerms: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null); setFieldErrors({});
    if (form.password !== form.confirmPassword) {
      setFieldErrors({ confirmPassword: "Passwords don't match" });
      return;
    }
    if (!form.industry) {
      setFieldErrors({ industry: "Select an industry" });
      return;
    }
    if (!form.agreedToTerms) {
      setError("Please agree to the terms");
      return;
    }
    setSubmitting(true);
    const res = await signup({
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      company: form.company || undefined,
      industry: form.industry,
      password: form.password,
      agreedToTerms: true,
    });
    setSubmitting(false);
    if (res.ok) navigate("/dashboard");
    else {
      setError(res.message);
      if (res.errors) {
        const fe: Record<string, string> = {};
        for (const e of res.errors) if (e.field) fe[e.field] = e.message;
        setFieldErrors(fe);
      }
    }
  }

  return (
    <section className="auth-shell container">
      <div className="auth-card card">
        <h1>Create your account</h1>
        <p className="auth-card__sub">Get started with SilicoInformatics.</p>
        <form className="form" onSubmit={onSubmit}>
          {error && <div className="form__alert" role="alert">{error}</div>}
          <div className="field__row">
            <div className="field">
              <label htmlFor="firstName">First name</label>
              <input id="firstName" required value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
              {fieldErrors.firstName && <span className="field__error">{fieldErrors.firstName}</span>}
            </div>
            <div className="field">
              <label htmlFor="lastName">Last name</label>
              <input id="lastName" required value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
              {fieldErrors.lastName && <span className="field__error">{fieldErrors.lastName}</span>}
            </div>
          </div>
          <div className="field">
            <label htmlFor="email">Email</label>
            <input id="email" type="email" autoComplete="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            {fieldErrors.email && <span className="field__error">{fieldErrors.email}</span>}
          </div>
          <div className="field">
            <label htmlFor="company">Company (optional)</label>
            <input id="company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="industry">Industry</label>
            <select id="industry" required value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value as Industry })}>
              <option value="">Select an industry…</option>
              {INDUSTRIES.map((i) => (
                <option key={i} value={i}>
                  {i.charAt(0).toUpperCase() + i.slice(1)}
                </option>
              ))}
            </select>
            {fieldErrors.industry && <span className="field__error">{fieldErrors.industry}</span>}
          </div>
          <div className="field__row">
            <div className="field">
              <label htmlFor="password">Password</label>
              <input id="password" type="password" autoComplete="new-password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              {fieldErrors.password && <span className="field__error">{fieldErrors.password}</span>}
            </div>
            <div className="field">
              <label htmlFor="confirmPassword">Confirm password</label>
              <input id="confirmPassword" type="password" autoComplete="new-password" required value={form.confirmPassword} onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })} />
              {fieldErrors.confirmPassword && <span className="field__error">{fieldErrors.confirmPassword}</span>}
            </div>
          </div>
          <label className="checkbox">
            <input type="checkbox" checked={form.agreedToTerms} onChange={(e) => setForm({ ...form, agreedToTerms: e.target.checked })} />
            <span>I agree to the terms of service and privacy policy.</span>
          </label>
          <button type="submit" className="btn btn--primary" disabled={submitting}>
            {submitting ? "Creating account…" : "Create account"}
          </button>
          <p className="form__footer">
            Already have an account? <Link to="/login">Sign in</Link>
          </p>
        </form>
      </div>
    </section>
  );
}
